// PRE RMA — Module CPN (Consultation Prénatale), indépendant du RCE.
// Même principe que age.ts/tranches.ts/rma.ts pour le RCE : aucune valeur
// numérique "en dur" ici, tout vient de RULES.cpn (rules.config.ts).
//
// Origine : fiche RMA CPN (Tableau 12) + guide officiel de remplissage du
// registre CPN fournis par le client, repris de l'outil de saisie autonome
// PRE_RMA_CPN.html déjà validé avec lui avant cette intégration. Les
// libellés ci-dessous sont volontairement identiques à ceux de cet outil
// (et à ceux de la fiche RMA CPN), pour que l'export reste reconnaissable.

import { RULES } from './constants.ts';

/** Les 21 indicateurs à un clic de l'écran Saisie CPN. */
export const CPN_INDICATEURS = [
  'cpn1', 'cpnPrecoce',
  'cpn4', 'cpn5Plus', 'mms', 'palu',
  'tpi1', 'tpi2', 'tpi3', 'tpi4Plus', 'mid',
  'hta', 'diabeteDepistage', 'diabetique',
  'syphilisTest', 'syphilisPositif', 'vihTest', 'vihPositif',
  'refAc', 'refSup',
  'td1', 'td2Plus',
] as const;
export type CpnIndicateur = (typeof CPN_INDICATEURS)[number];

/** Libellés affichés pour chaque indicateur (écran Saisie CPN, export Excel « Saisie CPN »). */
export const CPN_INDICATEUR_LABELS: Record<CpnIndicateur, { label: string; court: string }> = {
  cpn1: { label: 'CPN1', court: 'CPN1' },
  cpnPrecoce: { label: 'CPN précoce (< 4 mois)', court: 'CPN précoce' },
  cpn4: { label: 'CPN4', court: 'CPN4' },
  cpn5Plus: { label: 'CPN5 et +', court: 'CPN5+' },
  mms: { label: 'Reçu MMS', court: 'MMS' },
  palu: { label: 'Test positif paludisme', court: 'Palu+' },
  tpi1: { label: 'TPI 1', court: 'TPI1' },
  tpi2: { label: 'TPI 2', court: 'TPI2' },
  tpi3: { label: 'TPI 3', court: 'TPI3' },
  tpi4Plus: { label: 'TPI 4 et +', court: 'TPI4+' },
  mid: { label: 'Reçu MID', court: 'MID' },
  hta: { label: 'Diagnostiquée HTA', court: 'HTA' },
  diabeteDepistage: { label: 'Dépistage diabète', court: 'Dép. diabète' },
  diabetique: { label: 'Dépistée diabétique', court: 'Diabétique' },
  syphilisTest: { label: 'Testée syphilis', court: 'Test syphilis' },
  syphilisPositif: { label: 'Dépistée syphilis (+)', court: 'Syphilis+' },
  vihTest: { label: 'Testée VIH', court: 'Test VIH' },
  vihPositif: { label: 'VIH (+)', court: 'VIH+' },
  refAc: { label: 'Référée par AC, reçue au CSB', court: 'Réf. AC→CSB' },
  refSup: { label: 'Référée au niveau supérieur', court: 'Réf. niv. sup.' },
  td1: { label: 'Td1', court: 'Td1' },
  td2Plus: { label: 'Td2 et +', court: 'Td2+' },
};

/** Cases dont cocher l'une coche automatiquement l'autre (gain de clics, cohérence des données). */
export const CPN_IMPLICATIONS: Partial<Record<CpnIndicateur, CpnIndicateur>> = {
  cpnPrecoce: 'cpn1',
  syphilisPositif: 'syphilisTest',
  vihPositif: 'vihTest',
};

export interface CpnEntreeInput {
  numeroRegistre: number | null;
  ageAns: number | null;
  indicateurs: Partial<Record<CpnIndicateur, boolean>>;
}

export interface CpnValidationResult {
  ok: boolean;
  erreurs: string[];
}

/** Case cochée : true, 1, ou texte « true » / « 1 » / « oui » / « x » (valeur collée depuis un tableur ou relue d'un ancien stockage). */
function estCoche(v: unknown): boolean {
  if (v === true || v === 1) return true;
  if (typeof v === 'string') return ['true', '1', 'oui', 'o', 'x', 'vrai'].includes(v.trim().toLowerCase());
  return false;
}

/** Normalise les indicateurs reçus (accepte un objet partiel) en un enregistrement complet. */
export function normaliserIndicateurs(brut: Partial<Record<string, unknown>> | null | undefined): Record<CpnIndicateur, boolean> {
  const out = {} as Record<CpnIndicateur, boolean>;
  for (const cle of CPN_INDICATEURS) out[cle] = brut ? estCoche(brut[cle]) : false;
  return out;
}

/**
 * Valide une entrée CPN (équivalent serveur de la validation du formulaire).
 * Ne valide jamais uniquement côté client (même principe que validation.ts
 * pour le RCE) : cette même fonction tourne côté backend, dans les routes.
 */
export function validerCpn(input: { numeroRegistre: number | null; ageAns: number | null }): CpnValidationResult {
  const erreurs: string[] = [];
  const bornes = RULES.cpn.ageBornes;

  if (input.ageAns === null || input.ageAns === undefined || Number.isNaN(input.ageAns)) {
    erreurs.push('Âge requis.');
  } else if (!Number.isInteger(input.ageAns) || input.ageAns < bornes.min || input.ageAns > bornes.max) {
    erreurs.push(`Âge invalide : doit être un nombre entier entre ${bornes.min} et ${bornes.max} ans.`);
  }

  if (
    input.numeroRegistre === null ||
    input.numeroRegistre === undefined ||
    Number.isNaN(input.numeroRegistre) ||
    !Number.isInteger(input.numeroRegistre) ||
    input.numeroRegistre <= 0
  ) {
    erreurs.push('N° de registre CPN requis (nombre entier positif).');
  }

  return { ok: erreurs.length === 0, erreurs };
}

/** Tranche d'âge CPN1 (RULES.cpn.tranchesCpn1) : '<15 ans' | '15-17 ans' | '18 ans et plus'. */
export function trancheCpn1(ageAns: number): string {
  for (const b of RULES.cpn.tranchesCpn1) {
    if (ageAns <= b.jusquaAns) return b.label;
  }
  return RULES.cpn.tranchesCpn1[RULES.cpn.tranchesCpn1.length - 1].label;
}
export const TRANCHES_CPN1 = RULES.cpn.tranchesCpn1.map((b) => b.label);

export interface CpnRow {
  ageAns: number;
  indicateurs: Record<CpnIndicateur, boolean>;
}

/** Âge CPN sûr : entier >= 0, ou null (absent, non numérique, négatif). Jamais de tranche « par défaut » pour un âge inconnu. */
export function ageAnsValide(v: unknown): number | null {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
  const n = typeof v === 'number' ? v : Number(String(v).trim().replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : null;
}

/** Ligne CPN quelconque (base SQL, IndexedDB, fichier…) → ligne exploitable par le calcul ; `ageAns` null si inutilisable. */
export function normaliserLigneCpn(brut: { ageAns?: unknown; indicateurs?: unknown } | null | undefined): { ageAns: number | null; indicateurs: Record<CpnIndicateur, boolean> } {
  return {
    ageAns: ageAnsValide(brut?.ageAns),
    indicateurs: normaliserIndicateurs((brut?.indicateurs ?? null) as Partial<Record<string, unknown>> | null),
  };
}

export interface RmaCpnResultat {
  totalCpn: number;
  premiereCpn: {
    total: number;
    parTranche: Record<string, number>;
    precoce: number;
    /** Femmes en 1ère CPN dont l'âge est absent/invalide : comptées dans `total`, mais dans AUCUNE tranche (total = somme des tranches + nonVentilees). */
    nonVentilees: number;
  };
  suivi: {
    mms: number;
    /** [1er cpn + mms] : femmes en CPN1 ayant reçu FAF/MMS (les deux cases cochées sur la même ligne). */
    cpn1Mms: number;
    /** Femmes enceintes ayant reçu au moins 30 cp MMS/FAF : toutes les lignes codées « mms » (une case MMS = une cure de 30 cp). */
    mmsAu30: number;
    cpn4EtPlus: number;
    dontCpn4: number;
    dontCpn5Plus: number;
    paludismePositif: number;
  };
  tpiEtMid: { tpi1: number; tpi2: number; tpi3: number; tpi4EtPlus: number; mid: number };
  htaDiabete: { hta: number; diabeteDepistage: number; diabetique: number };
  syphilisVih: { syphilisTest: number; syphilisPositif: number; vihTest: number; vihPositif: number };
  references: { referesAcRecusCsb: number; referesNiveauSuperieur: number };
  td: { td1: number; td2EtPlus: number };
}

/**
 * Calcule le RMA CPN complet à partir des lignes de saisie — jamais de
 * saisie manuelle du RMA lui-même (même principe que calculerRmaComplet
 * pour le RCE) : c'est toujours un calcul en direct sur les données brutes.
 *
 * Règle "4 CPN et plus" : une femme cochée CPN4 ET CPN5 et + sur la même
 * ligne ne compte qu'UNE fois dans le total (elle ne peut pas être comptée
 * deux fois dans un même effectif), mais compte dans chacun des deux
 * détails "dont". Décision reprise telle quelle de l'outil de saisie
 * autonome validé avec le client.
 */
export function calculerRmaCpn(brutes: ReadonlyArray<{ ageAns?: unknown; indicateurs?: unknown } | null | undefined> | null | undefined): RmaCpnResultat {
  const rows = (Array.isArray(brutes) ? brutes : []).map(normaliserLigneCpn);
  const compte = (cle: CpnIndicateur) => rows.filter((r) => r.indicateurs[cle]).length;

  // Une seule passe : chaque 1ère CPN va dans UNE tranche, ou dans « nonVentilees » si l'âge est inutilisable.
  const parTranche: Record<string, number> = Object.fromEntries(TRANCHES_CPN1.map((l) => [l, 0]));
  let nonVentilees = 0;
  for (const r of rows) {
    if (!r.indicateurs.cpn1) continue;
    if (r.ageAns === null) nonVentilees++;
    else parTranche[trancheCpn1(r.ageAns)]++;
  }

  return {
    totalCpn: rows.length,
    premiereCpn: {
      total: compte('cpn1'),
      parTranche,
      precoce: compte('cpnPrecoce'),
      nonVentilees,
    },
    suivi: {
      mms: compte('mms'),
      cpn1Mms: rows.filter((r) => r.indicateurs.cpn1 && r.indicateurs.mms).length,
      mmsAu30: compte('mms'),
      cpn4EtPlus: rows.filter((r) => r.indicateurs.cpn4 || r.indicateurs.cpn5Plus).length,
      dontCpn4: compte('cpn4'),
      dontCpn5Plus: compte('cpn5Plus'),
      paludismePositif: compte('palu'),
    },
    tpiEtMid: {
      tpi1: compte('tpi1'),
      tpi2: compte('tpi2'),
      tpi3: compte('tpi3'),
      tpi4EtPlus: compte('tpi4Plus'),
      mid: compte('mid'),
    },
    htaDiabete: {
      hta: compte('hta'),
      diabeteDepistage: compte('diabeteDepistage'),
      diabetique: compte('diabetique'),
    },
    syphilisVih: {
      syphilisTest: compte('syphilisTest'),
      syphilisPositif: compte('syphilisPositif'),
      vihTest: compte('vihTest'),
      vihPositif: compte('vihPositif'),
    },
    references: {
      referesAcRecusCsb: compte('refAc'),
      referesNiveauSuperieur: compte('refSup'),
    },
    td: {
      td1: compte('td1'),
      td2EtPlus: compte('td2Plus'),
    },
  };
}

/** Ligne à plat (libellé + valeur), pour l'export Excel/PDF et l'affichage écran RMA CPN. */
export type LigneRmaCpn = { section: string } | { label: string; valeur: number };

export function ligneRmaCpnAPlat(r: RmaCpnResultat): LigneRmaCpn[] {
  const lignes: LigneRmaCpn[] = [];
  lignes.push({ section: 'Total CPN' });
  lignes.push({ label: 'Total CPN', valeur: r.totalCpn });

  lignes.push({ section: 'Femmes enceintes vues en 1ère CPN' });
  lignes.push({ label: 'Femmes enceintes vues en 1ère CPN', valeur: r.premiereCpn.total });
  for (const label of TRANCHES_CPN1) {
    lignes.push({ label: `Femmes enceintes ${label} vues en 1ère CPN`, valeur: r.premiereCpn.parTranche[label] ?? 0 });
  }
  if (r.premiereCpn.nonVentilees > 0) {
    lignes.push({ label: 'Femmes enceintes vues en 1ère CPN, âge non renseigné (non ventilées par tranche)', valeur: r.premiereCpn.nonVentilees });
  }
  lignes.push({ label: 'Femmes enceintes moins de 4 mois de grossesse vues en 1ère CPN', valeur: r.premiereCpn.precoce });

  lignes.push({ section: 'CPN, MMS, paludisme' });
  lignes.push({ label: 'Femmes enceintes vues en 1ère CPN ayant reçu FAF/MMS', valeur: r.suivi.mms });
  lignes.push({ label: 'Femmes enceintes ayant effectué 4 CPN et plus', valeur: r.suivi.cpn4EtPlus });
  lignes.push({ label: '   dont femmes enceintes ayant effectué 4 CPN', valeur: r.suivi.dontCpn4 });
  lignes.push({ label: '   dont femmes enceintes ayant effectué 5 CPN et plus', valeur: r.suivi.dontCpn5Plus });
  lignes.push({ label: 'Femmes enceintes testées positives pour le paludisme', valeur: r.suivi.paludismePositif });

  lignes.push({ section: 'TPI et MID' });
  lignes.push({ label: 'Femmes enceintes vues en 1ère CPN ayant eu MID', valeur: r.tpiEtMid.mid });
  lignes.push({ label: 'Femmes enceintes ayant reçu : TPI1', valeur: r.tpiEtMid.tpi1 });
  lignes.push({ label: 'Femmes enceintes ayant reçu : TPI2', valeur: r.tpiEtMid.tpi2 });
  lignes.push({ label: 'Femmes enceintes ayant reçu : TPI3', valeur: r.tpiEtMid.tpi3 });
  lignes.push({ label: 'Femmes enceintes ayant reçu : TPI4 et plus', valeur: r.tpiEtMid.tpi4EtPlus });

  lignes.push({ section: 'HTA et diabète' });
  lignes.push({ label: 'Femmes enceintes vues en CPN, diagnostiquées hypertendues', valeur: r.htaDiabete.hta });
  lignes.push({ label: 'Femmes enceintes vues en CPN, dépistées du diabète', valeur: r.htaDiabete.diabeteDepistage });
  lignes.push({ label: 'Femmes enceintes vues en CPN, diabétiques', valeur: r.htaDiabete.diabetique });

  lignes.push({ section: 'Syphilis et VIH' });
  lignes.push({ label: 'Femmes enceintes vues en 1ère CPN ayant effectué la sérologie de la syphilis', valeur: r.syphilisVih.syphilisTest });
  lignes.push({ label: 'Femmes enceintes vues en 1ère CPN dépistées à la syphilis (+)', valeur: r.syphilisVih.syphilisPositif });
  lignes.push({ label: 'Femmes enceintes vues en 1ère CPN ayant fait le test VIH', valeur: r.syphilisVih.vihTest });
  lignes.push({ label: 'Femmes enceintes vues en 1ère CPN dépistées VIH (+)', valeur: r.syphilisVih.vihPositif });

  lignes.push({ section: 'Références' });
  lignes.push({ label: 'Femmes enceintes référées par les AC reçues par les CSB', valeur: r.references.referesAcRecusCsb });
  lignes.push({ label: 'Femmes enceintes référées au niveau supérieur', valeur: r.references.referesNiveauSuperieur });

  lignes.push({ section: 'Td' });
  lignes.push({ label: 'Total Td1', valeur: r.td.td1 });
  lignes.push({ label: 'Total Td2 et +', valeur: r.td.td2EtPlus });

  return lignes;
}
